import type { AppGraph, AppGraphNode } from '../types'
import { NODE_TYPE_COLOR } from '../node-style'
import { useStudioLocale } from '@/i18n'

export interface NodeDetailPanelProps {
  graph: AppGraph
  selectedId: string | null
}

export function NodeDetailPanel({ graph, selectedId }: NodeDetailPanelProps) {
  const { strings } = useStudioLocale()
  const copy = strings.appMap
  const node = selectedId
    ? graph.nodes.find((n) => n.id === selectedId)
    : undefined

  if (!node) {
    return (
      <section className="border-b border-border/40 px-3 py-3" aria-labelledby="node-detail-heading">
        <h3 id="node-detail-heading" className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {copy.nodeDetail.title}
        </h3>
        <p className="text-xs text-muted-foreground">{copy.nodeDetail.empty}</p>
      </section>
    )
  }

  const incoming = graph.edges.filter((e) => e.target === node.id)
  const outgoing = graph.edges.filter((e) => e.source === node.id)

  return (
    <section className="border-b border-border/40 px-3 py-3" aria-labelledby="node-detail-heading">
      <h3 id="node-detail-heading" className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {copy.nodeDetail.title}
      </h3>

      <div className="mb-2 flex items-center gap-2">
        <span
          aria-hidden
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: NODE_TYPE_COLOR[node.type] }}
        />
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
          {copy.nodeTypes[node.type]}
        </span>
      </div>
      <p className="truncate text-sm font-semibold text-foreground" title={node.label}>
        {node.label}
      </p>
      <p className="mt-0.5 truncate text-[11px] text-muted-foreground" title={node.id}>
        {node.id}
      </p>

      {node.meta && Object.keys(node.meta).length > 0 && (
        <dl className="mt-3 space-y-0.5 text-xs">
          {Object.entries(node.meta).map(([k, v]) =>
            v === undefined ? null : (
              <div key={k} className="flex items-center justify-between gap-2">
                <dt className="text-muted-foreground">{k}</dt>
                <dd className="truncate font-mono text-foreground" title={String(v)}>
                  {String(v)}
                </dd>
              </div>
            ),
          )}
        </dl>
      )}

      <div className="mt-3 space-y-1 text-xs">
        <p className="text-muted-foreground">
          <span className="font-mono text-foreground">{incoming.length}</span>{' '}
          {copy.nodeDetail.incomingUnit} ·{' '}
          <span className="font-mono text-foreground">{outgoing.length}</span>{' '}
          {copy.nodeDetail.outgoingUnit}
        </p>
      </div>
    </section>
  )
}
